<template>
    <div class="box search-client">
        <confirm
            id="confirm-reward"
            ref="confirmReward"
            @confirm="confirmReward"
            @cancel="openClientPoints"
        >
            <template slot="title">{{ t('dashboard-searchClient-confirm_reward_title') }}</template>
            <template slot="body">
                <div v-html="t('dashboard-searchClient-confirm_reward_body', { 0: activeReward.description, 1: activeReward.price })"></div>
            </template>
        </confirm>
        <info-modal
            id="success-reward"
            ref="successReward"
            @close="resetRewards"
        >
            <template slot="title">{{ t('dashboard-searchClient-reward_success_title') }}</template>
            <template slot="body">
                <div v-html="t('dashboard-searchClient-reward_success_body', { 0: activeReward.description, 1: activeReward.price })"></div>
            </template>
        </info-modal>
        <info-modal id="client-points" ref="clientPoints" v-if="client">
            <template slot="title">
                <div v-html="t('dashboard-searchClient-client_points_title', { 0: client.first_name, 1: client.balance })"></div>
            </template>
            <template slot="body">
                <div class="result" v-if="rewards.length">
                    <div class="rewards">
                        <div
                            class="reward"
                            v-for="(reward, index) in rewards"
                            :key="index"
                        >
                            <span class="price">{{ reward.price }}</span>
                            <span class="title">{{ reward.description }}</span>
                            <div class="actions">
                                <button
                                    v-if="['insert'].includes(dashboardLevel)"
                                    :disabled="client.balance < reward.price"
                                    class="btn btn-primary btn-sm"
                                    @click.prevent="use(reward)"
                                >
                                    <i class="icon-check"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </info-modal>
        <info-modal id="client-coupons" ref="clientCoupons" v-if="client">
            <template slot="title">{{ t('dashboard-searchClient-client_coupons_title', { 0: client.first_name }) }}</template>
            <template slot="body">
                <div class="result">
                    <div class="coupons">
                        <div v-if="processing_coupons.length > 0">
                            <h2>{{ t('dashboard-searchClient-processing_coupons_title') }}</h2>
                            <p>
                                {{ t('dashboard-searchClient-processing_coupons_desc') }}
                            </p>
                            <div
                                v-for="(
                                    processing_coupon, index
                                ) in processing_coupons"
                                class="processing-coupon"
                                :key="index"
                            >
                                <div>
                                    <strong>{{
                                        processing_coupon.coupon_text
                                    }}</strong>
                                </div>
                                <small>
                                    {{ t('dashboard-searchClient-automation_origin', { 0: processing_coupon.automation_title }) }}
                                </small>
                            </div>
                        </div>
                        <div v-if="coupons.length > 0">
                            <h2>{{ t('dashboard-searchClient-active_coupons_title') }}</h2>
                            <div class="rewards">
                                <div
                                    v-for="(coupon, index) in coupons"
                                    class="reward"
                                    :key="index"
                                >
                                    <div class="title">
                                        <div>
                                            <span class="badge badge-info">{{
                                                coupon.code
                                            }}</span>
                                            <small
                                                :class="{
                                                    'text-danger':
                                                        !isCouponValid(coupon),
                                                }"
                                            >
                                                {{ t('dashboard-searchClient-validity') }}
                                                <human-date
                                                    :date="coupon.valid_from"
                                                    format="[d]/[M]/[Y] às [h]:[m] "
                                                />
                                                {{ t('dashboard-searchClient-until') }}
                                                <human-date
                                                    :date="
                                                        coupon.expiration_date
                                                    "
                                                    format="[d]/[M]/[Y] às [h]:[m] "
                                                />
                                            </small>
                                        </div>
                                        <div>
                                            <strong>{{
                                                coupon.description ||
                                                coupon.text
                                            }}</strong>
                                        </div>
                                    </div>
                                    <div v-if="['insert'].includes(dashboardLevel)" class="actions">
                                        <button
                                            v-show="isCouponValid(coupon)"
                                            class="btn btn-primary btn-sm"
                                            @click.prevent="
                                                validateCoupon(coupon.code)
                                            "
                                        >
                                            <i class="icon-check"></i>
                                        </button>
                                        <button
                                            v-show="!isCouponValid(coupon)"
                                            :disabled="true"
                                            class="btn btn-primary btn-sm"
                                            :title="invalidCouponError(coupon)"
                                        >
                                            <i class="icon-check"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </info-modal>
        <div :class="{ 'has-order': lastOrder && !!lastOrder.order.id }">
            <h3>{{ t('dashboard-searchClient-title') }}</h3>
            <div class="client-search-field">
                <label class="client-search-label">{{
                    t("filter_bar-client")
                }}</label>
                <clients-field
                    ref="clientsField"
                    v-model="clientId"
                    class="client-search-input"
                />
            </div>
            <template v-if="client && client.id">
                <div
                    v-if="module('sms') && !client.first_name"
                    class="info"
                >
                    <div class="alert alert-warning">
                        {{ t('dashboard-searchClient-not_registered') }}
                        <a
                            href="#"
                            type="button"
                            @click.prevent="
                                sendRegistrationCompletionLink(lastOrder)
                            "
                        >
                            <span
                                class="fa fa-share-square-o"
                                v-if="!sendingLink"
                            ></span>
                            <span
                                class="fa fa-spin fa-spinner"
                                v-if="sendingLink"
                            ></span>
                            {{ t('dashboard-searchClient-send_sms_link') }}
                        </a>
                    </div>
                </div>
                <div class="info" v-if="module('coupons')">
                    {{ t('dashboard-searchClient-coupons_label') }} <strong>{{ client.available_coupons }}</strong>
                    <span v-if="client.processing_coupons > 0">
                        {{ t('dashboard-searchClient-processing_label', { 0: client.processing_coupons }) }}
                    </span>
                    <a href="#" @click="openCoupons">{{ t('dashboard-searchClient-view_coupons') }}</a>
                </div>
                <div class="info" v-if="module('rewards') && pointsProgram()">
                    {{ t('dashboard-searchClient-points_label') }} <strong>{{ client.balance }}</strong>
                    <a href="#" @click="openRewards">{{ t('dashboard-searchClient-view_rewards') }}</a>
                </div>
                <div class="info" v-if="client.orders != null && client.orders !== ''">
                    {{ t('dashboard-searchClient-orders_label') }} <strong>{{ client.orders }}</strong>
                </div>
                <div class="info" v-if="client.birthdate">
                    {{ t('dashboard-searchClient-birthdate_label') }}
                    <strong
                        ><human-date
                            :date="client.birthdate"
                            format="[d]/[M]"
                    /></strong>
                </div>
                <div class="info" v-if="client.email">
                    {{ t('dashboard-searchClient-email_label') }} <strong>{{ client.email }}</strong>
                </div>
                <div class="info" v-if="client.whatsapp">
                    {{ t('dashboard-searchClient-phone_label') }} <strong>{{ client.whatsapp }}</strong>
                </div>
                <div class="buttons">
                    <register-event
                        v-if="$module('custom_events')"
                        @loading="
                            ($event) => {
                                loading = $event;
                            }
                        "
                        :client="client"
                    />
                    <router-link
                        :to="'/client/' + client.id"
                        class="btn btn-primary"
                        ><span class="fa fa-user"></span> {{ t('dashboard-searchClient-view_profile') }}</router-link
                    >
                </div>
            </template>
        </div>
    </div>
</template>
<script>
import keviApi from "../../keviApi";
import RegisterEvent from "./../../custom-events/register-event";
import { mapGetters } from "vuex";
import HumanDate from "../../components/human-date/human-date";
import ClientsField from "../../clients/clients-field";

export default {
    props: {
        lastOrder: {
            type: Object,
            default: {},
        },
    },
    components: {
        HumanDate,
        "register-event": RegisterEvent,
        "clients-field": ClientsField,
    },
    computed: {
        place() {
            return this.$store.state.place;
        },
        user() {
            return this.$store.state.user;
        },
        usedCoupons() {
            return this.$store.state.coupons.usedCoupons;
        },
        dashboardLevel(){
            return this.$store.state.user.permissions.dashboard.options.level;
        },
        ...mapGetters(["module", "pointsProgram"]),
    },
    data: () => ({
        client: {
            id: null,
            first_name: null,
            last_name: null,
            balance: null,
            available_coupons: null,
            processing_coupons: null,
            orders: null,
            birthdate: null,
            email: null,
            whatsapp: null,
        },
        activeReward: {
            id: null,
            description: null,
            price: null,
        },
        valid: true,
        loading: false,
        rewards: [],
        coupons: [],
        processing_coupons: [],
        clientId: null,
        sendingLink: false,
        permit_action_mixpanel: true,
        appMode: window.appMode,
    }),
    mounted() {
        const clientStr = localStorage.getItem("client");
        if (clientStr && clientStr !== "undefined") {
            try {
                const stored = JSON.parse(clientStr);
                if (stored && stored.id) {
                    this.clientId = stored.id;
                }
            } catch (e) {
                localStorage.removeItem("client");
            }
        }
    },
    methods: {
        isCouponValid(coupon) {
            const now = new Date();
            return (
                now >= new Date(coupon.valid_from) &&
                now <= new Date(coupon.expiration_date) &&
                coupon.used === "0"
            );
        },
        invalidCouponError(coupon) {
            const now = new Date();
            if (now < new Date(coupon.valid_from)) {
                return this.t('dashboard-searchClient-coupon_not_yet_valid');
            }
            if (now > new Date(coupon.expiration_date)) {
                return this.t('dashboard-searchClient-coupon_expired');
            }
            return "";
        },
        selectClient(clientId, options = {}) {
            if (options.clicked === true) {
                this.permit_action_mixpanel = true;
            }
            if (!clientId) {
                localStorage.removeItem("client");
                this.client = null;
                return;
            }
            const request = keviApi.getOptions(
                "dashboard/get-client/" + clientId,
            );
            this.$http(request).then(function (response) {
                let lastClient = null;
                let clientStr = localStorage.getItem("client");
                if (clientStr && clientStr !== "undefined") {
                    lastClient = JSON.parse(clientStr);
                }
                this.client = response.body.client;
                localStorage.setItem("client", JSON.stringify(this.client));
                if (
                    this.permit_action_mixpanel &&
                    lastClient?.id != this.client.id
                ) {
                    window.mixpanel?.track("Client Search", {
                        client_id: this.client.clients_id,
                        client_phone_number: this.client.phone,
                        client_name: this.client.first_name || "",
                        client_last_name: this.client.last_name || "",
                        client_completed_registration: !!this.client.first_name,
                        place_id: this.place.id,
                        place_name: this.user.place_name,
                        place_branch_name: this.user.branch_title,
                        user_name: this.user.name,
                        user_username: this.user.username,
                        user_email: this.user.email,
                    });
                }
            });
        },
        openRewards: function () {
            this.loading = true;
            let options = keviApi.getOptions("rewards/get-by-client", "GET", {
                clients_id: this.client.id,
            });
            this.$http(options).then(
                (response) => {
                    this.loading = false;
                    this.rewards = response.body.rewards.map((v) => {
                        v.price = parseInt(v.price);
                        return v;
                    });
                    this.client.balance = response.body.client.balance;
                    this.client.balance = this.client.balance
                        ? parseInt(this.client.balance)
                        : 0;
                    this.$refs.clientPoints.open();
                },
                () => {
                    this.loading = false;
                    this.valid = false;
                },
            );
        },

        openCoupons: function () {
            this.loading = true;
            let options = keviApi.getOptions(
                "coupons-codes/get-by-client",
                "GET",
                {
                    clients_id: this.client.id,
                },
            );
            this.$http(options).then(
                (response) => {
                    this.loading = false;
                    this.coupons = response.body.coupons;
                    this.processing_coupons = response.body.processing_coupons;
                    this.$refs.clientCoupons.open();
                },
                () => {
                    this.loading = false;
                    this.valid = false;
                },
            );
        },
        closeCoupons: function () {
            this.$refs.clientCoupons.close();
        },
        validateCoupon: function (code) {
            this.$refs.clientCoupons.close();
            this.$store.dispatch("coupons/validateCode", code);
        },
        openClientPoints: function () {
            this.$refs.clientPoints.open();
        },
        use: function (reward) {
            this.activeReward = reward;
            this.$refs.clientPoints.close();
            this.$refs.confirmReward.open();
        },
        confirmReward: function () {
            let options = keviApi.getOptions("rewards/use", "PUT", {
                client_id: this.client.id,
                reward_id: this.activeReward.id,
            });
            this.$http(options).then((response) => {
                let reward = this.rewards.find(
                    (reward) => reward.id == this.activeReward.id,
                );
                window.mixpanel?.track("Use Reward", {
                    client_id: this.client.clients_id,
                    client_phone_number: this.client.phone,
                    client_name: this.client.first_name,
                    client_last_name: this.client.last_name,
                    client_completed_registration: !!this.client.first_name,
                    reward_id: reward.id,
                    reward_description: reward.description,
                    reward_place_id: reward.place_id,
                    reward_price: reward.price,
                    reward_status: response.status,
                    place_id: this.place.id,
                    place_name: this.user.place_name,
                    place_branch_name: this.user.branch_title,
                    user_name: this.user.name,
                    user_username: this.user.username,
                    user_email: this.user.email,
                });
                this.$refs.successReward.open();
                this.permit_action_mixpanel = false;
                this.selectClient(this.client.id);
            });
        },
        resetRewards: function () {
            this.activeReward = {
                id: null,
                description: null,
                price: null,
            };
            this.rewards = [];
        },
        sendRegistrationCompletionLink() {
            this.sendingLink = true;
            let options = keviApi.getOptions(
                "/client/send-registration-completion-link",
                "POST",
                {
                    clients_id: this.client.id,
                },
            );
            this.$http(options).then(function (response) {
                let info = response.body.info;
                window.mixpanel?.track("Send Registration Completion Link", {
                    client_id: this.client.clients_id,
                    client_phone_number: this.client.phone,
                    client_name: this.client.first_name || "",
                    client_last_name: this.client.last_name || "",
                    client_number_of_orders: info.orders,
                    client_available_coupons: info.available_coupons,
                    client_points_balance: info.balance,
                    link_status: response.body.status || null,
                    link_url: response.body.link || null,
                    place_id: this.place.id,
                    place_name: this.user.place_name,
                    place_branch_name: this.user.branch_title,
                    user_name: this.user.name,
                    user_username: this.user.username,
                    user_email: this.user.email,
                });
                this.sendingLink = false;
                if (response.body.status === "success") {
                    return this.$store.dispatch("notification/success", {
                        text: this.t('dashboard-searchClient-sms_success'),
                        duration: 5000,
                    });
                }
                this.$store.dispatch("notification/warning", {
                    text: this.t('dashboard-searchClient-sms_error'),
                    duration: 5000,
                });
            });
        },
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
    },
    watch: {
        clientId(newValue, oldValue) {
            if (newValue === oldValue) {
                return;
            }
            if (newValue) {
                this.permit_action_mixpanel = true;
            }
            this.selectClient(newValue, { clicked: !!newValue });
        },
        lastOrder: function (newValue) {
            if (newValue && newValue.client) {
                const nextId = newValue.client.id;
                if (String(this.clientId) === String(nextId)) {
                    // Force refresh when the same client places a new order
                    this.selectClient(nextId);
                } else {
                    this.clientId = nextId;
                }
            }
            if (
                newValue &&
                (!newValue.mixpanel || newValue.mixpanel == false)
            ) {
                this.permit_action_mixpanel = false;
            }
        },
        usedCoupons() {
            if (this.client && this.client.id) {
                this.selectClient(this.client.id);
            }
        },
    },
};
</script>
